// BOORO: the Chief. One agent per space that knows the team and hands out the
// work; the widget reaches it from every area of the app.
import { CHIEF_COLOR, withTaskRows } from "@booro/tasks";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type {
  ChiefAgent,
  LoginFieldInput,
  TaskThreadEntry,
  ThreadSnapshot,
} from "@rakazo/contracts";
import { taskWorkingRunStatus } from "@rakazo/contracts";
import { isSecretAskBlock } from "@rakazo/core";
import { BotAvatar, Button, Input } from "@rakazo/ui-web";
import { MessageSquare, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AskBlock } from "../../components/AskCard";
import { AskCard } from "../../components/AskCard";
import { Shimmer } from "../../components/ai/primitives";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { officeRpc, openAgentChat } from "./org-scope";
import { TaskRow } from "./task-thread";

/** Runs a reload for every event of a stream, reconnecting when it drops. */
function useLiveReload(
  subscribe: ((signal: AbortSignal) => Promise<AsyncIterable<unknown>>) | null,
  reload: () => Promise<void>,
) {
  useEffect(() => {
    if (!subscribe) return;
    let active = true;
    const controller = new AbortController();
    void reload().catch(() => undefined);
    void (async () => {
      while (active) {
        try {
          for await (const _event of await subscribe(controller.signal)) {
            if (!active) return;
            await reload().catch(() => undefined);
          }
        } catch {
          // The stream ends on close or a dropped connection; retry below.
        }
        if (!active) return;
        await new Promise((resolve) => setTimeout(resolve, 1_000));
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [subscribe, reload]);
}

/** The Chief, reachable from every area. */
export function ChiefWidget() {
  const { t } = useLingui();
  const [chief, setChief] = useState<ChiefAgent | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  // The Chief is only looked up here; it is created when the person opens it.
  useEffect(() => {
    let active = true;
    void rpc.booroChief
      .find()
      .then((next) => {
        if (active) setChief(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  async function openPanel() {
    setOpen(true);
    if (chief) return;
    try {
      setChief(await rpc.booroChief.ensure());
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  return (
    <>
      {open ? null : (
        <button
          type="button"
          data-testid="chief-widget"
          aria-label={t`Open the Chief`}
          title={t`Open the Chief`}
          onClick={() => void openPanel()}
          className="fixed bottom-5 end-5 z-30 flex h-14 w-14 items-center justify-center rounded-full border border-border bg-card shadow-lg hover:bg-muted"
        >
          <BotAvatar
            color={chief?.color ?? CHIEF_COLOR}
            identity={chief?.botId ?? "chief"}
            size={30}
          />
        </button>
      )}
      {open ? <ChiefPanel chief={chief} error={error} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ChiefPanel({
  chief,
  error: openError,
  onClose,
}: {
  chief: ChiefAgent | null;
  error: string;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [snapshot, setSnapshot] = useState<ThreadSnapshot | null>(null);
  const [tasks, setTasks] = useState<TaskThreadEntry[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const botId = chief?.botId ?? null;
  // The Chief lives in one office; it is always reached through that office.
  const client = useMemo(() => (chief ? officeRpc(chief.spaceId) : rpc), [chief]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  const loadThread = useCallback(async () => {
    if (!botId) return;
    setSnapshot(await client.threads.get({ botId }));
  }, [botId, client]);
  const loadTasks = useCallback(async () => {
    if (!botId) return;
    setTasks(await client.booroChief.tasks({ botId }));
  }, [botId, client]);

  const subscribeThread = useCallback(
    async (signal: AbortSignal) => {
      const head = await client.threads.head({ botId: botId ?? "" });
      return client.threads.subscribe({ botId: botId ?? "", cursor: head.cursor }, { signal });
    },
    [botId, client],
  );
  const subscribeBoard = useCallback(
    (signal: AbortSignal) => client.boards.subscribe(undefined, { signal }),
    [client],
  );
  useLiveReload(botId ? subscribeThread : null, loadThread);
  useLiveReload(botId ? subscribeBoard : null, loadTasks);

  const messages = snapshot?.messages ?? [];
  const rows = withTaskRows(messages, (message) => message.createdAt, tasks, {
    historyComplete: snapshot?.olderCursor === null,
  });
  const working = Boolean(taskWorkingRunStatus(snapshot?.run?.status)) || sending;

  // The newest entry stays in view, like in the chat.
  const rowCount = rows.length;
  useEffect(() => {
    if (rowCount === 0 && !working) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [rowCount, working]);

  async function send() {
    if (!botId || !draft.trim()) return;
    setSending(true);
    setError("");
    try {
      await client.threads.send({ botId, text: draft.trim() });
      setDraft("");
      await loadThread();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setSending(false);
    }
  }

  async function answer(
    runId: string,
    messageId: string,
    text: string,
    fields?: LoginFieldInput[],
    remember?: false,
    dismiss?: true,
  ) {
    if (!botId) return;
    await client.threads.answer({
      botId,
      runId,
      messageId,
      answer: text,
      ...(fields ? { fields } : {}),
      ...(remember === false ? { remember } : {}),
      ...(dismiss ? { dismiss } : {}),
    });
    await loadThread();
  }

  const shownError = error || openError;

  return (
    <div
      role="dialog"
      aria-label={chief?.name ?? t`Chief`}
      data-testid="chief-panel"
      className="fixed inset-0 z-40 flex flex-col border-border bg-background shadow-xl md:inset-auto md:end-5 md:bottom-5 md:h-[560px] md:max-h-[calc(100dvh-2.5rem)] md:w-[400px] md:rounded-2xl md:border"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <BotAvatar
          color={chief?.color ?? CHIEF_COLOR}
          identity={chief?.botId ?? "chief"}
          size={26}
          status={taskWorkingRunStatus(snapshot?.run?.status) ?? undefined}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-medium text-foreground" dir="auto">
            {chief?.name ?? t`Chief`}
          </p>
          {chief?.title ? (
            <p className="truncate text-[12.5px] text-muted-foreground" dir="auto">
              {chief.title}
            </p>
          ) : null}
        </div>
        {botId ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t`Open the full chat`}
            title={t`Open the full chat`}
            onClick={() => {
              onClose();
              if (chief) openAgentChat(chief.spaceId, `/app/${botId}`);
            }}
          >
            <MessageSquare />
          </Button>
        ) : null}
        <Button size="icon-sm" variant="ghost" aria-label={t`Close`} onClick={onClose}>
          <X />
        </Button>
      </div>

      <div ref={scrollRef} className="rk-scroll flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {snapshot && rows.length === 0 ? (
          <p className="text-[13.5px] text-muted-foreground">
            <Trans>Say what should be done.</Trans>
          </p>
        ) : null}
        {rows.map((row) => {
          if ("kind" in row) {
            return (
              <TaskRow
                key={row.key}
                task={row.task}
                botId={botId ?? ""}
                client={client}
                spaceId={chief?.spaceId}
                onChanged={loadTasks}
                onOpen={() => {
                  const assignee = row.task.assignee?.botId;
                  if (!assignee) return;
                  onClose();
                  if (chief) {
                    openAgentChat(
                      chief.spaceId,
                      `/app/${assignee}?task=${encodeURIComponent(row.task.ticketId)}`,
                    );
                  }
                }}
              />
            );
          }
          const message = row;
          if (message.role === "system") return null;
          const mine = message.role === "user";
          const text = message.blocks
            .flatMap((block) => (block.kind === "text" ? [block.text] : []))
            .join("\n\n")
            .trim();
          const asks = message.blocks.filter((block): block is AskBlock => block.kind === "ask");
          if (!text && asks.length === 0) return null;
          return (
            <div key={message.id} className={`flex flex-col gap-2 ${mine ? "items-end" : ""}`}>
              {text ? (
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[14px] ${
                    mine ? "bg-chat-user text-chat-user-foreground" : "bg-muted text-foreground"
                  }`}
                  dir="auto"
                >
                  {mine ? (
                    <p className="whitespace-pre-wrap break-words">{text}</p>
                  ) : (
                    <ChatMarkdown>{text}</ChatMarkdown>
                  )}
                </div>
              ) : null}
              {asks.map((block, index) => (
                <AskCard
                  key={index}
                  block={block}
                  canAnswer={block.status !== "answered" && Boolean(message.runId)}
                  canDismiss={
                    block.status !== "answered" && Boolean(message.runId) && isSecretAskBlock(block)
                  }
                  onAnswer={(value, fields, remember) =>
                    answer(message.runId ?? "", message.id, value, fields, remember)
                  }
                  onDismiss={() =>
                    answer(message.runId ?? "", message.id, "", undefined, undefined, true)
                  }
                />
              ))}
            </div>
          );
        })}
        {working ? (
          <p className="text-[13.5px]">
            <Shimmer>
              <Trans>Working…</Trans>
            </Shimmer>
          </p>
        ) : null}
        {shownError ? (
          <p role="alert" className="text-[13px] text-destructive">
            {shownError}
          </p>
        ) : null}
      </div>

      <form
        className="flex items-center gap-2 border-t border-border px-3 py-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <Input
          autoFocus
          aria-label={t`What should be done?`}
          placeholder={t`What should be done?`}
          value={draft}
          disabled={!botId}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" size="sm" disabled={!botId || sending || !draft.trim()}>
          <Trans>Send</Trans>
        </Button>
      </form>
    </div>
  );
}
